import {
  Capacitor,
  type PluginListenerHandle,
  registerPlugin,
} from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import type { TaskStatus } from "@vidimo/shared/enums";

/**
 * Bridge to the native iOS Live Activity. The Swift plugin
 * (`apps/mobile/ios/App/App/LiveActivityPlugin.swift`) drives a WidgetKit
 * Live Activity that mirrors the task on the lock screen + Dynamic Island.
 *
 * Lifecycle ownership: the DB is the single source of truth. The React live
 * view drives `start`/`end` off the task's status; the activity is a mirror.
 * A Pause/Resume/Next-step tap *from* the activity (iOS 17 App Intents) writes
 * straight to `POST /tasks/:id/action` so the change reaches the DB with
 * the app closed; only if that POST fails (offline) is it queued natively (App
 * Group) and flushed into the canonical `setTaskStatus` write on next
 * foreground (see `task-activity.ts`). Finish always deep-links into the app.
 */

/** Static attributes — fixed for the life of the activity. */
export interface TaskActivityAttributes {
  /** Cleaning kind / task-type label, e.g. "Turnover". */
  kindLabel: string;
  /** Property name shown on the started line, e.g. "Emerald Valley". */
  propertyName?: string;
  /** "Started 10:12" — work-start clock (fixed once the task starts). */
  startedLabel?: string;
  taskId: string;
  /** Short unit label, e.g. "Apt 202". */
  unitLabel: string;
}

/**
 * Dynamic content — the card is checklist-driven ("progress, not time"): it
 * surfaces step progress + the current step, never an elapsed timer.
 */
export interface TaskActivityContentState {
  /** Title of the first not-yet-done step, e.g. "Dust & wipe surfaces". */
  currentStep?: string | null;
  /** `TaskChecklistItem.id` of `currentStep` — carried so the intent can
   *  record which step was tapped, making a lost-ack replay idempotent. */
  currentStepId?: string | null;
  /** Guest ETA, only when it differs from nominal check-in. */
  etaText?: string | null;
  /** The step after `currentStep`, so "Next step" can advance optimistically. */
  nextStep?: string | null;
  /** `TaskChecklistItem.id` of `nextStep` — advanced into `currentStepId` by
   *  the optimistic native update so rapid lock-screen taps each carry the
   *  correct step identity without waiting for a JS state push. */
  nextStepId?: string | null;
  status: TaskStatus;
  /** Checklist progress — drives the "3 / 7" header + the progress bar. */
  stepsDone: number;
  stepsTotal: number;
  /** Compact highlighted prep cue, e.g. "Baby cot". */
  topCue?: string | null;
}

/** A Finish or Undo tap the operator made on the locked-screen activity (or a
 *  Pause an older build queued), drained into the canonical writes on the next
 *  foreground of the account it was made under. The deep-link fallback
 *  (`deep-link.ts`) shares the union and still accepts `advance`. */
export interface PendingLiveActivityAction {
  action: "pause" | "resume" | "advance" | "finish";
  taskId: string;
  /** Epoch ms of the tap (native clock); used only for ordering/debug. */
  ts: number;
}

/** The per-activity APNs push token iOS issued for a task's Live Activity. */
interface LiveActivityPushToken {
  taskId: string;
  /** Hex-encoded APNs token, ready to POST to the API. */
  token: string;
}

export interface LiveActivityPlugin {
  /** Subscribe to per-activity push-token issuance + rotation. */
  addListener: (
    eventName: "liveActivityPushToken",
    listener: (data: LiveActivityPushToken) => void
  ) => Promise<PluginListenerHandle>;
  /** Read + clear the queued lock-screen button taps, returning only those
   *  made under `userId`'s auth context. */
  drainPendingActions: (options: {
    userId: string;
  }) => Promise<{ actions: PendingLiveActivityAction[] }>;
  /** End the activity. The final `state` (when given) is shown during the
   *  brief dismissal window — e.g. the green "Done" card on completion. */
  end: (options: {
    taskId: string;
    state?: TaskActivityContentState;
  }) => Promise<void>;
  /** End every activity at once and clear the auth context and queued taps
   *  the App Group holds, for an account leaving the device. */
  forgetAccount: () => Promise<void>;
  /** Whether Live Activities are enabled on this device. */
  isSupported: () => Promise<{ supported: boolean }>;
  /** Stash the API base URL + session token so the background intent can write
   *  through to the server directly (no app open). A store naming another
   *  account, or naming none and holding another token, first ends every
   *  activity and drops the queued taps; one naming none and holding this
   *  token has its taps claimed for `userId`, but for an older build's
   *  `advance`, which is dropped. */
  setAuthContext: (options: {
    apiBaseUrl: string;
    token: string;
    userId: string;
  }) => Promise<void>;
  /** Patch only `status` on a running activity, leaving the step / cue / ETA
   *  content the live view pushed intact. No-op when nothing is running. */
  setStatus: (options: { status: TaskStatus; taskId: string }) => Promise<void>;
  /** Start-or-update: requests a new activity for `taskId`, or updates the
   *  existing one if it's already running (idempotent). */
  start: (options: {
    attributes: TaskActivityAttributes;
    state: TaskActivityContentState;
  }) => Promise<{ activityId?: string }>;
}

const plugin = registerPlugin<LiveActivityPlugin>("LiveActivity");

const isIos = () => Capacitor.getPlatform() === "ios";

/**
 * Platform-guarded facade so callers never branch on platform or worry about
 * a missing/erroring native plugin. On web + Android every method is a safe
 * no-op (the feature is iOS-only — Android parity is a later pass).
 */
export const liveActivity = {
  /**
   * Subscribe to per-activity push-token issuance + rotation. Resolves to
   * an unsubscribe fn on success (a no-op fn on non-iOS, where there's nothing to
   * attach), or `null` when the native listener fails to attach — so the caller
   * can retry rather than latch a failed attach.
   */
  async addPushTokenListener(
    handler: (token: LiveActivityPushToken) => void
  ): Promise<(() => void) | null> {
    if (!isIos()) {
      return () => {
        // no-op on web/Android
      };
    }
    try {
      const sub = await plugin.addListener("liveActivityPushToken", handler);
      return () => {
        void sub.remove();
      };
    } catch (error) {
      console.warn("[live-activity] addPushTokenListener failed", error);
      return null;
    }
  },

  async drainPendingActions(
    userId: string
  ): Promise<PendingLiveActivityAction[]> {
    if (!isIos()) {
      return [];
    }
    try {
      return (await plugin.drainPendingActions({ userId })).actions;
    } catch {
      return [];
    }
  },

  async end(taskId: string, state?: TaskActivityContentState): Promise<void> {
    if (!isIos()) {
      return;
    }
    try {
      await plugin.end({ state, taskId });
    } catch (error) {
      console.warn("[live-activity] end failed", error);
    }
  },

  async forgetAccount(): Promise<void> {
    if (!isIos()) {
      return;
    }
    try {
      await plugin.forgetAccount();
    } catch (error) {
      console.warn("[live-activity] forgetAccount failed", error);
    }
  },

  async isSupported(): Promise<boolean> {
    if (!isIos()) {
      return false;
    }
    try {
      return (await plugin.isSupported()).supported;
    } catch {
      return false;
    }
  },

  async setAuthContext(
    apiBaseUrl: string,
    token: string,
    userId: string
  ): Promise<void> {
    if (!isIos()) {
      return;
    }
    try {
      await plugin.setAuthContext({ apiBaseUrl, token, userId });
    } catch (error) {
      console.warn("[live-activity] setAuthContext failed", error);
    }
  },

  /**
   * Patch a running activity's status without touching the rest of the card.
   * The canonical status writes call this from every surface; the live view
   * still owns `start` and the content, since it holds the view model.
   */
  async setStatus(taskId: string, status: TaskStatus): Promise<void> {
    if (!isIos()) {
      return;
    }
    try {
      await plugin.setStatus({ status, taskId });
    } catch (error) {
      console.warn("[live-activity] setStatus failed", error);
    }
  },

  async start(
    attributes: TaskActivityAttributes,
    state: TaskActivityContentState
  ): Promise<void> {
    if (!isIos()) {
      return;
    }
    try {
      await plugin.start({ attributes, state });
    } catch (error) {
      // A card that never appears is otherwise indistinguishable from a feature
      // nobody used: there is no console on a TestFlight build, and iOS reports
      // the reason (Live Activities switched off, a missing entitlement, the
      // per-app cap) only here. Report it so it is diagnosable without a cable.
      console.warn("[live-activity] start failed", error);
      const enabled = await liveActivity.isSupported();
      Sentry.captureException(
        new Error(`Live Activity start failed: ${String(error)}`),
        {
          extra: { activitiesEnabled: enabled, taskId: attributes.taskId },
          // One issue for the whole failure mode, not one per task.
          fingerprint: ["live-activity-start-failed"],
          tags: { activitiesEnabled: String(enabled), area: "live-activity" },
        }
      );
    }
  },
};
